
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { IAlertFilter, IAlertOrder, buildAlertFilterGroups, FilterGroup, IFilter, IFilterRequest } from '../../../modules/alerts/filter';
import { IAlert } from '../../models/alert.model';
import { map, Observable, of, timestamp } from 'rxjs';
import { IAlertPagination } from '../../state/alert/alert.states';
import { DateTime } from 'luxon';
import { PAGE_SIZE } from '../../constants/filter';
import { DateRange } from '@angular/material/datepicker';

export interface IAlertLockRequest {
  alert_ids: string[],
  user_id: string,
  username: string
}

export interface IAlertResponse {
  alerts: IAlert[],
  total: number
}

export interface ICommentResponse {
  ids: string[]
  comment_ids?: string[],
  user_id: string,
  name: string,
  text: string,
  timestamp: string,
  should_dismiss_alert: boolean,
}
export type IDismissAlertsRequest = {
  alertIds: string[];
  userId: string;
  dismissTimestamp: string;
}

export type IEscalateAlertsRequest = {
  user_id: string,
  username: string,
  alert_ids: string[],
  comment?: string | undefined
}

export type IScoutReviewRequest = {
  scoutType: string,
  filters: IFilterRequest,
  notes: string | null,
}

export type IAddCommentToAlertsRequest = {
  user_id: string,
  username: string,
  alert_ids: string[],
  text: string,
  should_dismiss_alert: boolean
}

export type IMarkAlertsViewedRequest = {
  ids: string[],
  user_id: string,
  timestamp: string,
  update_map: {
    viewed: boolean,
    viewed_by: string,
    view_timestamp: string
  }
}

const TEXT_RESPONSE_TYPE: any = {
  responseType: 'text'
};

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  static readonly endpoints = {
    filter: `${environment.apiUrl}/AlertAPI/Filter`,
    addCommentToAlerts: `${environment.apiUrl}/AlertAPI/AddCommentToAlerts`,
    dismiss: `${environment.apiUrl}/AlertAPI/Dismiss`,
    dismissFilter: `${environment.apiUrl}/alerts/dismiss`,
    escalateFilter: `${environment.apiUrl}/alerts/escalate`,
    scoutReviewFilter: `${environment.apiUrl}/scout/review/filter-alerts`,
    update: `${environment.apiUrl}/alert/update`,
    muteAllActive: `${environment.apiUrl}/alert/mute-all-active`
  };

  httpClient = inject(HttpClient);

  filterAlerts(organisationID: string, page: IAlertPagination, filter: IAlertFilter, perPage: number = PAGE_SIZE): Observable<IAlertResponse> {
    const pagination = { per_page: perPage, page_num: page.current };
    const fieldOrdering = filter.ordering ?? [];
    const groups = buildAlertFilterGroups(filter, organisationID);

    return this.httpClient.post(AlertService.endpoints.filter, { groups, pagination, ordering: fieldOrdering }).pipe(
      map((e: any) => ({ alerts: e["items"], total: e["total"] }))
    );
  }

  filterAlertsByGroups(groups: FilterGroup[], page: IAlertPagination, ordering: IAlertOrder[]): Observable<IAlertResponse> {
    const pagination = { per_page: PAGE_SIZE, page_num: page.current };
    return this.httpClient.post(AlertService.endpoints.filter, { groups, pagination, ordering }).pipe(
      map((e: any) => ({ alerts: e["items"], total: e["total"] }))
    );
  }

  dismissByFilter(filter: IFilterRequest) {
    return this.httpClient.post(AlertService.endpoints.dismissFilter, { filters: filter })
  }

  escalateByFilter(filter: IFilterRequest) {
    return this.httpClient.post(AlertService.endpoints.escalateFilter, { filters: filter })
  }

  scoutReviewFilter(data: IScoutReviewRequest) {
    const request = {
      scout_type: data.scoutType,
      filters: data.filters,
      notes: data.notes
    }
    return this.httpClient.post(AlertService.endpoints.scoutReviewFilter, request)
  }

  dismiss(dismissRequest: IDismissAlertsRequest): Observable<any> {
    let body = {
      ids: dismissRequest.alertIds,
      user_id: dismissRequest.userId,
      dismiss_timestamp: DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss')
    };
    const config: any = {
      responseType: 'text'
    }
    return this.httpClient.post<any>(AlertService.endpoints.dismiss, body, config)
  }

  // NOTE: will consolidate dismiss functions once AD flow has been updated
  dismissUsingUpdate(dismissRequest: IDismissAlertsRequest, viewTimestamp: string): Observable<any> {
    const userId = dismissRequest.userId;
    const updateMap = {
      ids: dismissRequest.alertIds,
      user_id: userId,
      timestamp: dismissRequest.dismissTimestamp,
      update_map: {
        dismissed_by: userId,
        dismissed: true,
        dismiss_timestamp: dismissRequest.dismissTimestamp,
        viewed: true,
        viewed_by: userId,
        view_timestamp: viewTimestamp
      }
    };
    const body = {
      updates: updateMap,
      comments: [] // NOTE: API can't handle missing comments property
    };

    return this.httpClient.post<any>(AlertService.endpoints.update, body, TEXT_RESPONSE_TYPE);
  }

  escalate(escalateRequest: IEscalateAlertsRequest, viewTimestamp: string): Observable<any> {
    let comments: any[] = [];
    const userId = escalateRequest.user_id;

    if (escalateRequest.comment != null && escalateRequest.comment !== "") {
      comments = escalateRequest.alert_ids.map(e => {
        return {
          'alert_id': e,
          'user_id': userId,
          'text': escalateRequest.comment,
          'name': escalateRequest.username
        }
      })
    }
    const timestamp = DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');
    let updates = {
      'ids': escalateRequest.alert_ids,
      'user_id': userId,
      'update_map': {
        escalated: true,
        escalation_timestamp: timestamp,
        escalated_by: userId,
        viewed: true,
        viewed_by: userId,
        view_timestamp: viewTimestamp
      },
      timestamp: timestamp,
    }

    const config: any = {
      responseType: 'text'
    }
    return this.httpClient.post<any>(AlertService.endpoints.update, {
      'updates': updates,
      'comments': comments
    }, config)
  }

  addCommentToAlerts(addCommentsRequest: IAddCommentToAlertsRequest): Observable<ICommentResponse> {
    let body: any = {
      ids: addCommentsRequest.alert_ids ? addCommentsRequest.alert_ids : [],
      user_id: addCommentsRequest.user_id,
      name: addCommentsRequest.username,
      text: addCommentsRequest.text,
      timestamp: DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss'),
      should_dismiss_alert: addCommentsRequest.should_dismiss_alert
    };

    return this.httpClient.post(AlertService.endpoints.addCommentToAlerts, body).pipe(
      map((response: any) => {
        body["comment_ids"] = response["comment"].map((comment: any) => comment["comment_id"]);
        return body;
      })
    )
  }

  markAlertsViewed(request: IMarkAlertsViewedRequest): Observable<any> {
    const config: any = {
      responseType: 'text'
    }
    return this.httpClient.post<any>(AlertService.endpoints.update, {
      'updates': request,
      'comments': []
    }, config)
  }

  muteAllActiveAlerts(cameraIDs: string[], reason: string): Observable<string[]> {
    return this.httpClient.post(AlertService.endpoints.muteAllActive, {
      camera_ids: cameraIDs,
      comment: reason
    }).pipe(
      map((response: any) => {
        return response["ids"]
      })
    )
  }

  getAlertsForBookmarks(
    organisationId: string,
    cameraIds: string[],
    dateRange: DateRange<Date>
  ): Observable<IAlert[]> {
    if (dateRange.start == null || dateRange.end == null ||
        Number.isNaN(dateRange.start.getTime()) || Number.isNaN(dateRange.end.getTime())) {
      return of([]);
    }
    const groups: FilterGroup[] = [
      {
        logic: 'AND',
        filters: [
          { key: 'organisation_id', operator: '==', value: organisationId },
          { key: 'camera_id', operator: 'IN', value: cameraIds },
          { key: 'creation_timestamp', operator: '>=', value: dateRange.start!.toISOString() },
          { key: 'creation_timestamp', operator: '<=', value: dateRange.end!.toISOString() },
        ],
      },
    ];

    const pagination = { per_page: 500, page_num: 1 };
    const ordering: IAlertOrder[] = [{ key: 'creation_timestamp', direction: 'ASC' }];

    return this.httpClient.post(AlertService.endpoints.filter, { groups, pagination, ordering }).pipe(
      map((response: any) => response['items'] as IAlert[])
    );
  }
}
